// FeedbackForm.js
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Rating,
  Box,
  Alert
} from '@mui/material';
import { submitBetaFeedback } from './apiService';

function FeedbackForm({ open, onClose }) {
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async () => {
    if (!feedback.trim()) {
      setError('Please provide some feedback');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await submitBetaFeedback({
        rating,
        feedback,
        email: email || undefined,
      });

      setSuccess(true);
      setTimeout(() => {
        onClose();
        setRating(0);
        setFeedback('');
        setEmail('');
        setSuccess(false);
      }, 2000);
    } catch (err) {
      console.error('Error submitting feedback:', err);
      setError('Failed to submit feedback. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        Product feedback
        <Typography sx={{ color: '#52677A', fontSize: '0.9rem', fontWeight: 400, mt: 0.75, lineHeight: 1.6 }}>
          Tell us what is working and what is getting in the way — it shapes what we build next. You can also join our{' '}
          <a
            href="https://discord.gg/KveXyv9fCK"
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#0E4D6E', fontWeight: 600 }}
          >
            Discord community
          </a>
          {' '}for support and discussion.
        </Typography>
      </DialogTitle>
      <DialogContent>
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Thank you for your feedback! 🎉
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box
          sx={{
            mb: 2.5,
            p: 2,
            borderRadius: '12px',
            border: '1px solid #E6EDF2',
            backgroundColor: '#F8FBFD',
          }}
        >
          <Typography sx={{ color: '#13293A', fontWeight: 700, fontSize: '0.9rem', mb: 0.75 }}>
            How would you rate your experience so far?
          </Typography>
          <Rating
            value={rating}
            onChange={(event, newValue) => setRating(newValue)}
            size="large"
          />
        </Box>

        <TextField
          label="Your feedback"
          multiline
          rows={4}
          fullWidth
          value={feedback}
          onChange={(e) => setFeedback(e.target.value)}
          placeholder="What do you like? What needs improvement? Any bugs or issues?"
          sx={{ mb: 2 }}
        />

        <TextField
          label="Email (optional)"
          type="email"
          fullWidth
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="If you'd like us to follow up"
          helperText="We'll only use this to respond to your feedback"
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
        >
          {loading ? 'Submitting...' : 'Submit Feedback'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default FeedbackForm;
